import { type ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Badge } from '@/components/ui/badge';
import { useLocale, useViewMessages } from '@/lib/i18n';
import { confidenceTone, METRIC_INFO } from '@/lib/metric-info';
import { cn } from '@/lib/utils';

const CARD_W = 304;
const OPEN_DELAY_MS = 150;
const CLOSE_GRACE_MS = 100;

const MESSAGES = {
  nb: {
    high: 'Høy',
    medium: 'Middels',
    low: 'Lav',
    benchmark: 'Rettesnor',
    strong: 'Sterk',
    ok: 'Ok',
    weak: 'Svak',
    advice: 'Råd',
  },
  en: {
    high: 'High',
    medium: 'Medium',
    low: 'Low',
    benchmark: 'Benchmark',
    strong: 'Strong',
    ok: 'OK',
    weak: 'Weak',
    advice: 'Advice',
  },
};

/** Format a benchmark threshold for display (1 dp max + unit suffix). */
function fmtThresh(n: number, unit: 'pct' | 'ratio' | 'seconds'): string {
  const v = Math.round(n * 10) / 10;
  if (unit === 'pct') return `${v}%`;
  if (unit === 'seconds') return `${v}s`;
  return `${v}×`;
}

/**
 * Hoverable metric name. Wraps the label (or custom children) with a subtle
 * dotted-underline-on-hover affordance; after a 150ms hover delay it opens a
 * portaled card explaining WHAT the metric is, HOW it's calculated (formula),
 * WHERE the data comes from, and a confidence badge with a one-line reason.
 *
 * The card is portaled to <body> with fixed positioning (mirrors CaveatRef) so
 * it is never clipped by a table's overflow container; clamped to the viewport
 * and repositioned on scroll/resize. Closing has a 100ms grace so moving the
 * pointer onto the card keeps it open; Escape closes. Unknown metric keys fall
 * back to rendering the plain label/children — safe to wrap any column.
 */
export function MetricInfoTrigger({
  metricKey,
  label,
  className,
  children,
}: {
  metricKey: string;
  label: string;
  className?: string;
  children?: ReactNode;
}) {
  const { locale } = useLocale();
  const m = useViewMessages(MESSAGES);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLSpanElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const openTimer = useRef<number | null>(null);
  const closeTimer = useRef<number | null>(null);

  const entry = METRIC_INFO[metricKey];

  // Clear any pending timers on unmount (never setState on a dead component).
  useEffect(
    () => () => {
      if (openTimer.current !== null) window.clearTimeout(openTimer.current);
      if (closeTimer.current !== null) window.clearTimeout(closeTimer.current);
    },
    [],
  );

  useEffect(() => {
    if (!open) return;
    const place = () => {
      const r = triggerRef.current?.getBoundingClientRect();
      if (!r) return;
      const left = Math.min(
        Math.max(8, r.left + r.width / 2 - CARD_W / 2),
        window.innerWidth - CARD_W - 8,
      );
      setPos({ top: r.bottom + 6, left });
    };
    place();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    // Tap-opened cards (touch) need an explicit outside-press to dismiss, since
    // there's no pointer-leave on touch. Ignore presses inside trigger/card.
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t) || cardRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDoc);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open]);

  // Unknown key → plain rendering, zero affordance (safe to wrap any column).
  if (!entry) return <>{children ?? label}</>;

  const cancelClose = () => {
    if (closeTimer.current !== null) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  const scheduleOpen = () => {
    cancelClose();
    if (openTimer.current !== null) return;
    openTimer.current = window.setTimeout(() => {
      openTimer.current = null;
      setOpen(true);
    }, OPEN_DELAY_MS);
  };
  const scheduleClose = () => {
    if (openTimer.current !== null) {
      window.clearTimeout(openTimer.current);
      openTimer.current = null;
    }
    cancelClose();
    closeTimer.current = window.setTimeout(() => {
      closeTimer.current = null;
      setOpen(false);
    }, CLOSE_GRACE_MS);
  };
  // Tap support: touch devices have no hover, so a press toggles the card open/shut.
  const toggle = () => {
    if (openTimer.current !== null) {
      window.clearTimeout(openTimer.current);
      openTimer.current = null;
    }
    cancelClose();
    setOpen((o) => !o);
  };

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions lint/a11y/useKeyWithClickEvents: pointer-only definition affordance (can't become a nested interactive control); keyboard and screen-reader users get the full methodology sheet.
    <span
      ref={triggerRef}
      onMouseEnter={scheduleOpen}
      onMouseLeave={scheduleClose}
      onClick={(e) => {
        // Don't let the tap bubble to a parent control (e.g. the Ads sort button
        // this trigger can nest inside) — tapping the name only opens the card.
        e.stopPropagation();
        toggle();
      }}
      className={cn(
        'cursor-help decoration-fg-subtle decoration-dotted underline-offset-2 hover:underline',
        open && 'underline',
        className,
      )}
    >
      {children ?? label}
      {open
        ? createPortal(
            <div
              ref={cardRef}
              role="tooltip"
              onMouseEnter={cancelClose}
              onMouseLeave={scheduleClose}
              style={{
                position: 'fixed',
                top: pos.top,
                left: pos.left,
                width: CARD_W,
                maxHeight: `calc(100vh - ${pos.top + 8}px)`,
              }}
              className="z-50 overflow-y-auto rounded-md border border-border bg-surface p-3 text-left shadow-pop"
            >
              <span className="block text-label font-semibold text-fg">{label}</span>
              <span className="mt-1 block whitespace-normal text-label leading-relaxed text-fg-muted">
                {entry.what[locale]}
              </span>
              <span className="mt-2 block rounded bg-surface-2 px-1.5 py-0.5 text-label text-fg tabular">
                {locale === 'en' ? entry.formulaEn : entry.formula}
              </span>
              <span className="mt-2 flex items-center gap-1.5">
                <span aria-hidden className="size-1.5 shrink-0 rounded-full bg-fg-subtle" />
                <span className="whitespace-normal text-label text-fg-muted">
                  {entry.source[locale]}
                </span>
              </span>
              {entry.benchmark ? (
                <span className="mt-2 block">
                  <span className="mb-1 block text-micro font-medium uppercase tracking-wide text-fg-subtle">
                    {m.benchmark}
                  </span>
                  <span className="flex gap-1">
                    {(() => {
                      const b = entry.benchmark;
                      const ge = b.higherIsBetter;
                      const cells: {
                        tone: 'good' | 'warn' | 'bad';
                        label: string;
                        range: string;
                      }[] = [
                        {
                          tone: 'good',
                          label: m.strong,
                          range: `${ge ? '≥' : '≤'}${fmtThresh(b.good, b.unit)}`,
                        },
                        {
                          tone: 'warn',
                          label: m.ok,
                          range: `${ge ? '≥' : '≤'}${fmtThresh(b.warn, b.unit)}`,
                        },
                        {
                          tone: 'bad',
                          label: m.weak,
                          range: `${ge ? '<' : '>'}${fmtThresh(b.warn, b.unit)}`,
                        },
                      ];
                      return cells.map((c) => (
                        <span
                          key={c.tone}
                          className="flex flex-1 flex-col items-center gap-0.5 rounded bg-surface-2 px-1 py-1 text-center"
                        >
                          <Badge tone={c.tone}>{c.label}</Badge>
                          <span className="text-micro tabular text-fg-muted">{c.range}</span>
                        </span>
                      ));
                    })()}
                  </span>
                </span>
              ) : null}
              <span className="mt-2 block">
                <Badge tone={confidenceTone(entry.confidence)}>{m[entry.confidence]}</Badge>
              </span>
              <span className="mt-1 block whitespace-normal text-micro leading-relaxed text-fg-muted">
                {entry.confidenceWhy[locale]}
              </span>
              {entry.advice ? (
                <span className="mt-2 block border-t border-border pt-2">
                  <span className="mb-0.5 block text-micro font-medium uppercase tracking-wide text-fg-subtle">
                    {m.advice}
                  </span>
                  <span className="block whitespace-normal text-micro italic leading-relaxed text-fg-muted">
                    {entry.advice[locale]}
                  </span>
                </span>
              ) : null}
            </div>,
            document.body,
          )
        : null}
    </span>
  );
}
